import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CalendarClock, FileText, UploadCloud, Landmark, ShieldCheck, Users, LineChart, X, Sparkles, Loader2, FileDown, Eye, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { listPrevisionnels } from "@/lib/previsionnels";
import { listRapportsMensuels, ensureRapportMensuel, getRapportMensuel, saveRmSections, generateRm, pollRm, uploadPreuve, deletePreuve, downloadRmPdf } from "@/lib/rapports_mensuels";

const MOIS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const NOW = new Date();
const CLOTURE = new Date(NOW.getFullYear(), NOW.getMonth() - 1, 1);
const ANNEES = Array.from({ length: 6 }, (_, i) => NOW.getFullYear() - i);
const STAGE_LABEL = { preparation: "Préparation…", compilation: "Compilation du réalisé & du budget…", redaction: "Rédaction du rapport AJ (Gemini 3.1 Pro)…" };

const PREUVES = [
    { key: "releves_bancaires", title: "Relevés bancaires", icon: Landmark, hint: "Relevés de tous les comptes du mois (PDF, images ou tableurs)." },
    { key: "attestations", title: "Attestations URSSAF / TVA", icon: ShieldCheck, hint: "Preuves de paiement des dettes postérieures (URSSAF, TVA, salaires)." },
    { key: "balances_clients", title: "Balances âgées clients", icon: Users, hint: "État du poste clients et suivi du recouvrement." },
    { key: "suivi_commercial", title: "Suivi commercial", icon: LineChart, hint: "Carnet de commandes, tableaux de suivi commercial et social." },
];

export default function RapportsMensuelsTab({ dossierId }) {
    const [list, setList] = useState([]);
    const [scenario, setScenario] = useState("");
    const [mois, setMois] = useState(String(CLOTURE.getMonth()));
    const [annee, setAnnee] = useState(String(CLOTURE.getFullYear()));
    const [report, setReport] = useState(null);
    const [opening, setOpening] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [stage, setStage] = useState("");
    const timer = useRef(null);
    const skipSave = useRef(true);

    useEffect(() => {
        listPrevisionnels(dossierId).then((l) => { setList(l || []); if (l?.length) setScenario((s) => s || l[0].id); }).catch(() => {});
        listRapportsMensuels(dossierId).catch(() => {});
        return () => { if (timer.current) clearInterval(timer.current); };
    }, [dossierId]);

    async function open() {
        setOpening(true);
        try {
            const r = await ensureRapportMensuel(dossierId, { mois: Number(mois) + 1, annee: Number(annee), previsionnel_id: scenario || null });
            skipSave.current = true;
            setReport(r);
        } catch (e) { toast.error("Ouverture impossible", { description: e.message }); }
        finally { setOpening(false); }
    }

    const sections = report?.sections || [];
    const preuves = report?.preuves || [];
    const preuvesByCat = (key) => preuves.filter((p) => p.categorie === key);

    // Sauvegarde debouncée des sections éditées.
    useEffect(() => {
        if (skipSave.current) { skipSave.current = false; return; }
        if (!report?.id || generating) return;
        const t = setTimeout(() => { saveRmSections(report.id, report.sections || []).catch(() => {}); }, 1200);
        return () => clearTimeout(t);
    }, [report?.sections, report?.id, generating]); // eslint-disable-line react-hooks/exhaustive-deps

    const patchSection = (id, content) => { skipSave.current = false; setReport((r) => ({ ...r, sections: (r.sections || []).map((s) => (s.id === id ? { ...s, content } : s)) })); };

    async function addProof(categorie, files) {
        for (const file of Array.from(files)) {
            try {
                const preuve = await uploadPreuve(dossierId, report.id, categorie, file);
                setReport((r) => ({ ...r, preuves: [...(r.preuves || []), preuve] }));
                toast.success("Preuve déposée", { description: file.name });
            } catch (e) {
                toast.error(`Échec : ${file.name}`, { description: /bucket not found/i.test(e.message) ? "Bucket « dataroom » introuvable." : e.message });
            }
        }
    }

    async function removeProof(preuveId) {
        try {
            await deletePreuve(report.id, preuveId);
            setReport((r) => ({ ...r, preuves: (r.preuves || []).filter((p) => p.id !== preuveId) }));
        } catch (e) { toast.error("Suppression impossible", { description: e.message }); }
    }

    async function generate() {
        if (!report?.id) return;
        if (sections.length && !window.confirm("Régénérer écrasera le texte actuel du rapport. Continuer ?")) return;
        setGenerating(true); setStage("preparation");
        try {
            const { job_id, dossier_id } = await generateRm(report.id);
            timer.current = setInterval(async () => {
                try {
                    const s = await pollRm(job_id, dossier_id);
                    setStage(s.stage);
                    if (s.stage === "done") {
                        clearInterval(timer.current); setGenerating(false); skipSave.current = true;
                        setReport((r) => ({ ...r, sections: s.sections || [] }));
                        toast.success("Rapport mensuel rédigé", { description: "5 points de contrôle AJ. Affinez le texte puis exportez le PDF." });
                    } else if (s.stage === "error") {
                        clearInterval(timer.current); setGenerating(false); toast.error("Génération impossible", { description: s.error || "Réessayez." });
                    }
                } catch (e) { clearInterval(timer.current); setGenerating(false); toast.error("Suivi interrompu", { description: e.message }); }
            }, 2500);
        } catch (e) { setGenerating(false); toast.error("Lancement impossible", { description: e.message }); }
    }

    const exportPdf = useCallback(async () => {
        try { await downloadRmPdf(report.id); } catch (e) { toast.error("Export PDF impossible", { description: e.message }); }
    }, [report?.id]);

    return (
        <section data-testid="rapports-mensuels-tab" className="space-y-6">
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white"><CalendarClock className="h-5 w-5" strokeWidth={1.75} /></span>
                <div className="flex-1 min-w-[240px]">
                    <h2 className="font-display text-xl text-foreground">Rapports mensuels — Copilote de Clôture AJ</h2>
                    <p className="text-sm text-muted-foreground">Confrontez le réalisé du mois au budget prévisionnel et rédigez le rapport des 5 points de contrôle de l'Administrateur Judiciaire.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:grid-cols-4">
                <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mois de clôture</label>
                    <Select value={mois} onValueChange={setMois} disabled={!!report}>
                        <SelectTrigger data-testid="rm-mois-select" className="h-11 bg-background"><SelectValue /></SelectTrigger>
                        <SelectContent>{MOIS.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Année de clôture</label>
                    <Select value={annee} onValueChange={setAnnee} disabled={!!report}>
                        <SelectTrigger data-testid="rm-annee-select" className="h-11 bg-background"><SelectValue /></SelectTrigger>
                        <SelectContent>{ANNEES.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Scénario prévisionnel de référence</label>
                    <Select value={scenario} onValueChange={setScenario} disabled={!list.length || !!report}>
                        <SelectTrigger data-testid="rm-scenario-select" className="h-11 bg-background"><SelectValue placeholder={list.length ? "Choisir un scénario" : "Aucun prévisionnel"} /></SelectTrigger>
                        <SelectContent>{list.map((p) => <SelectItem key={p.id} value={p.id}>{p.titre || "Sans titre"}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div className="flex items-end">
                    {report ? (
                        <Button type="button" variant="outline" data-testid="rm-change-period" onClick={() => setReport(null)} className="h-11 w-full rounded-full">Changer de période</Button>
                    ) : (
                        <Button type="button" data-testid="rm-open" onClick={open} disabled={opening} className="h-11 w-full rounded-full bg-indigo-600 text-white hover:bg-indigo-700">
                            {opening ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5" strokeWidth={1.75} />} Ouvrir ce rapport
                        </Button>
                    )}
                </div>
            </div>

            {report && (
                <>
                    <div className="space-y-3">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Preuves de clôture — {MOIS[Number(mois)]} {annee}</h3>
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                            {PREUVES.map((cat) => (
                                <Dropzone key={cat.key} cat={cat} files={preuvesByCat(cat.key)} onAdd={(f) => addProof(cat.key, f)} onRemove={removeProof} />
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-indigo-200 bg-indigo-50/50 p-5">
                        <div className="flex items-center gap-2 text-sm text-indigo-900"><FileText className="h-4 w-4" strokeWidth={1.75} /> Le Copilote lit vos preuves, les confronte au budget et rédige les 5 points de contrôle AJ.</div>
                        <div className="flex items-center gap-2">
                            {sections.length > 0 && (
                                <Button type="button" variant="outline" data-testid="rm-export-pdf" onClick={exportPdf} className="h-11 rounded-full"><FileDown className="h-4 w-4" /> Générer le PDF pour l'AJ</Button>
                            )}
                            <Button type="button" data-testid="rm-generate" onClick={generate} disabled={generating || !preuves.length} className="h-11 rounded-full bg-indigo-600 text-white px-6 hover:bg-indigo-700 disabled:opacity-60">
                                {generating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" strokeWidth={1.75} />} {generating ? "Rédaction…" : "Rédiger le rapport mensuel (IA)"}
                            </Button>
                        </div>
                    </div>

                    {generating && (
                        <div data-testid="rm-progress" className="flex items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50/60 px-4 py-3 text-sm text-indigo-800">
                            <Loader2 className="h-4 w-4 animate-spin" /> {STAGE_LABEL[stage] || "Traitement…"} <span className="text-indigo-500">(jusqu'à 1 à 2 minutes)</span>
                        </div>
                    )}

                    {sections.length > 0 ? (
                        <div data-testid="rm-editor" className="space-y-3">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rapport éditable (5 points de contrôle AJ)</h3>
                            {sections.map((s, idx) => <SectionEditor key={s.id} section={s} idx={idx} onChange={(c) => patchSection(s.id, c)} />)}
                        </div>
                    ) : !generating && (
                        <div data-testid="rm-empty" className="rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-10 text-center text-sm text-muted-foreground">
                            Déposez les preuves ci-dessus puis cliquez sur « Rédiger le rapport mensuel (IA) ».
                        </div>
                    )}
                </>
            )}
        </section>
    );
}

function SectionEditor({ section, idx, onChange }) {
    const [preview, setPreview] = useState(false);
    return (
        <div data-testid={`rm-section-${idx}`} className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center gap-2 border-b border-slate-100 p-3">
                <span className="flex-1 font-semibold text-slate-800">{section.title}</span>
                <button type="button" data-testid={`rm-section-toggle-${idx}`} onClick={() => setPreview((p) => !p)} className="flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50">
                    {preview ? <><Pencil className="h-3.5 w-3.5" /> Éditer</> : <><Eye className="h-3.5 w-3.5" /> Aperçu</>}
                </button>
            </div>
            <div className="p-3">
                {preview ? (
                    <div className="prose prose-slate prose-sm max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{section.content || "_Vide_"}</ReactMarkdown></div>
                ) : (
                    <textarea data-testid={`rm-section-content-${idx}`} value={section.content || ""} onChange={(e) => onChange(e.target.value)}
                        className="min-h-[160px] w-full resize-y rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm leading-relaxed text-slate-800 focus:border-primary focus:outline-none" />
                )}
            </div>
        </div>
    );
}

function Dropzone({ cat, files, onAdd, onRemove }) {
    const inputRef = useRef(null);
    const [over, setOver] = useState(false);
    const Icon = cat.icon;
    return (
        <div data-testid={`rm-dropzone-${cat.key}`} className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
                <Icon className="h-4 w-4 text-indigo-600" strokeWidth={1.75} />
                <h4 className="text-sm font-semibold text-slate-700">{cat.title}</h4>
                {files.length > 0 && <span className="ml-auto text-xs text-slate-500">{files.length} fichier{files.length > 1 ? "s" : ""}</span>}
            </div>
            <div className="p-4">
                <button
                    type="button"
                    data-testid={`rm-dropzone-trigger-${cat.key}`}
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setOver(true); }}
                    onDragLeave={() => setOver(false)}
                    onDrop={(e) => { e.preventDefault(); setOver(false); if (e.dataTransfer.files?.length) onAdd(e.dataTransfer.files); }}
                    className={`flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-7 text-center transition-colors ${over ? "border-indigo-400 bg-indigo-50" : "border-slate-300 bg-slate-50 hover:border-indigo-300 hover:bg-indigo-50/50"}`}
                >
                    <UploadCloud className="h-6 w-6 text-slate-400" strokeWidth={1.5} />
                    <span className="text-sm font-medium text-slate-600">Glissez vos fichiers ou cliquez pour parcourir</span>
                    <span className="text-xs text-slate-400">{cat.hint}</span>
                </button>
                <input ref={inputRef} type="file" multiple data-testid={`rm-dropzone-input-${cat.key}`} onChange={(e) => { if (e.target.files?.length) onAdd(e.target.files); e.target.value = ""; }} className="hidden" />
                {files.length > 0 && (
                    <ul data-testid={`rm-filelist-${cat.key}`} className="mt-3 space-y-1.5">
                        {files.map((f, i) => (
                            <li key={f.id || `${f.nom_fichier}-${i}`} className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-1.5 text-xs text-slate-700">
                                <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                                <span className="flex-1 truncate">{f.nom_fichier}</span>
                                <button type="button" data-testid={`rm-file-remove-${cat.key}-${i}`} onClick={() => onRemove(f.id)} className="text-slate-400 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
